/**
 * Volunteer Auth Service
 * Volunteers are stored in the `volunteers` collection (separate from `staff`).
 * They have their own login portal and protected routes.
 */
import { signInWithEmailAndPassword, signOut, sendPasswordResetEmail } from 'firebase/auth';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../utils/firebase';

export const loginVolunteer = async (email, password, expectedOrgId = null) => {
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  const user = userCredential.user;

  // Primary lookup: by UID (the standard for programmatically-created accounts)
  let volunteerSnap = await getDoc(doc(db, 'volunteers', user.uid));

  // Fallback lookup: by email field (handles manually-created Firestore records
  // where the document ID does not match the Firebase Auth UID)
  if (!volunteerSnap.exists()) {
    const q = query(collection(db, 'volunteers'), where('email', '==', user.email));
    const snap = await getDocs(q);
    if (!snap.empty) {
      volunteerSnap = snap.docs[0];
    }
  }

  if (!volunteerSnap.exists()) {
    await signOut(auth);
    throw new Error('Volunteer account not found. Please contact your organisation.');
  }

  const userData = volunteerSnap.data();

  if (expectedOrgId && userData.orgId !== expectedOrgId) {
    await signOut(auth);
    throw new Error(`Access denied. This login is for ${expectedOrgId} volunteers only.`);
  }

  let orgName = userData.orgId;
  const orgDoc = await getDoc(doc(db, 'organizations', userData.orgId));
  if (orgDoc.exists()) {
    orgName = orgDoc.data().branding?.name || orgName;
  }

  // Store auth UID on the volunteer doc so notifications can use it
  if (volunteerSnap.id !== user.uid) {
    updateDoc(doc(db, 'volunteers', volunteerSnap.id), { authUid: user.uid }).catch(() => {});
  }
  return { user: { uid: user.uid, id: volunteerSnap.id, email: user.email, ...userData, orgName } };
};

/** Look up the volunteer profile for the currently-signed-in Firebase user.
 *  Tries UID-keyed doc first; falls back to email query for manually-created records. */
const getVolunteerProfile = async (uid, email) => {
  let snap = await getDoc(doc(db, 'volunteers', uid));
  if (!snap.exists()) {
    const q = query(collection(db, 'volunteers'), where('email', '==', email));
    const result = await getDocs(q);
    if (!result.empty) snap = result.docs[0];
  }
  return snap.exists() ? snap : null;
};

export const getCurrentVolunteer = async () => {
  if (!auth.currentUser) return null;
  const snap = await getVolunteerProfile(auth.currentUser.uid, auth.currentUser.email);
  if (!snap) return null;
  return { uid: auth.currentUser.uid, id: snap.id, email: auth.currentUser.email, ...snap.data() };
};

export const isVolunteerAuthenticated = async () => {
  if (!auth.currentUser) return false;
  const snap = await getVolunteerProfile(auth.currentUser.uid, auth.currentUser.email);
  return snap !== null;
};

export const logoutVolunteer = async () => {
  await signOut(auth);
  return true;
};

export const resetVolunteerPassword = async (email) => {
  await sendPasswordResetEmail(auth, email);
  return true;
};
